import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Radio, Video, Send, Loader2, StopCircle } from "lucide-react";
import { api, imgUrl, wsUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function downsample(input, inRate, outRate) {
  if (!input.length || outRate >= inRate) return input;
  const ratio = inRate / outRate;
  const length = Math.floor(input.length / ratio);
  const out = new Float32Array(length);
  for (let i = 0; i < length; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j += 1) sum += input[j];
    out[i] = sum / Math.max(1, end - start);
  }
  return out;
}

function pcmToB64(samples) {
  const bytes = new Uint8Array(samples.length * 2);
  const view = new DataView(bytes.buffer);
  for (let i = 0; i < samples.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  let raw = "";
  for (let i = 0; i < bytes.length; i += 1) raw += String.fromCharCode(bytes[i]);
  return btoa(raw);
}

export default function GoLive() {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const chatRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);

  const [title, setTitle] = useState("");
  const [selected, setSelected] = useState([]);
  const [session, setSession] = useState(null);
  const [live, setLive] = useState(false);
  const [viewers, setViewers] = useState(0);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [spotlightId, setSpotlightId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [camera, setCamera] = useState("off");
  const [mic, setMic] = useState("off");

  const { data: products = [] } = useQuery({
    queryKey: ["my-products-live", user?.id],
    queryFn: async () => (await api.get(`/products?seller_id=${user.id}&limit=100`)).data,
    enabled: Boolean(user?.id),
  });

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight });
  }, [messages]);

  useEffect(() => {
    const video = videoRef.current;
    if (!live || !video || !streamRef.current) return;
    video.srcObject = streamRef.current;
    video.play().catch(() => {});
  }, [live, camera]);

  useEffect(() => () => stopEverything(), []);

  const toggleProduct = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const attachSocketHandlers = (ws) => {
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "chat") setMessages((m) => [...m.slice(-50), msg]);
        else if (msg.type === "presence") {
          setViewers(msg.viewer_count);
          if (msg.system) setMessages((m) => [...m.slice(-50), { system: msg.system }]);
        }
      } catch {
        /* ignore malformed frames */
      }
    };
  };

  const openLiveSocket = (sessionId) =>
    new Promise((resolve, reject) => {
      const ws = new WebSocket(wsUrl(`/api/ws/live/${sessionId}?role=seller&name=${encodeURIComponent(user?.name || "Seller")}`));
      wsRef.current = ws;
      const timer = setTimeout(() => {
        ws.close();
        reject(new Error("Live connection timed out"));
      }, 8000);
      ws.onopen = () => {
        clearTimeout(timer);
        intervalRef.current = setInterval(sendFrame, 350);
        startAudio(streamRef.current);
        if (selected[0]) {
          ws.send(JSON.stringify({ type: "spotlight", product_id: selected[0] }));
        }
        attachSocketHandlers(ws);
        resolve(ws);
      };
      ws.onerror = () => {
        clearTimeout(timer);
        reject(new Error("Live connection failed"));
      };
    });

  const stopAudio = () => {
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  };

  const startAudio = (stream) => {
    stopAudio();
    if (!stream || !stream.getAudioTracks().length) return;
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;
    const silent = ctx.createGain();
    silent.gain.value = 0;
    processor.onaudioprocess = (event) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== 1) return;
      const input = event.inputBuffer.getChannelData(0);
      const pcm = downsample(input, ctx.sampleRate, 16000);
      ws.send(JSON.stringify({ type: "audio", rate: 16000, data: pcmToB64(pcm) }));
    };
    source.connect(processor);
    processor.connect(silent);
    silent.connect(ctx.destination);
    ctx.resume().catch(() => {});
  };

  const allowCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamera("missing");
      toast.error("This browser has no camera access. Open Go Live in Chrome or Edge.");
      return false;
    }
    try {
      stopAudio();
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      let stream;
      let heard = false;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        heard = stream.getAudioTracks().length > 0;
      } catch (both) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        const blocked = both?.name === "NotAllowedError" || both?.name === "PermissionDeniedError";
        setMic(blocked ? "blocked" : "missing");
        if (blocked) {
          toast.message("Camera is on. Allow the microphone in the browser popup so shoppers can hear you.");
        }
      }
      streamRef.current = stream;
      setCamera("on");
      if (heard) setMic("on");
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      if (wsRef.current?.readyState === 1) startAudio(stream);
      return true;
    } catch (e) {
      streamRef.current = null;
      const blocked = e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError";
      setCamera(blocked ? "blocked" : "missing");
      toast.error(blocked
        ? "Camera is blocked. Use the camera icon in the address bar, choose Allow, then press Allow camera again."
        : "No camera was found on this device.");
      return false;
    }
  };

  const startLive = async () => {
    if (!title.trim()) return toast.error("Add a title for your live");
    if (selected.length === 0) return toast.error("Select at least one product to feature");
    setStarting(true);
    let created = null;
    try {
      if (camera !== "on") {
        const ok = await allowCamera();
        if (!ok) {
          toast.message("Going live without a camera. Shoppers see the product photo until you allow it.");
        }
      }

      const { data } = await api.post("/live/sessions", { title: title.trim(), product_ids: selected });
      created = data;
      setSession(data);
      setSpotlightId(selected[0]);
      setLive(true);
      setViewers(1);
      setMessages([]);

      await openLiveSocket(data.id);
      toast.success("You're live");
    } catch (e) {
      stopEverything();
      setCamera("off");
      setMic("off");
      setLive(false);
      setSession(null);
      if (created?.id) {
        api.post(`/live/sessions/${created.id}/end`).catch(() => {});
      }
      toast.error(e?.message === "Live connection failed" || e?.message === "Live connection timed out"
        ? "Could not open the live room. Check that the API is running with WebSockets enabled."
        : (apiError(e) || "Could not start the live room"));
    } finally {
      setStarting(false);
    }
  };

  const sendFrame = () => {
    const video = videoRef.current, canvas = canvasRef.current, ws = wsRef.current;
    if (!video || !canvas || !ws || ws.readyState !== 1 || video.videoWidth === 0) return;
    canvas.width = 640;
    canvas.height = (video.videoHeight / video.videoWidth) * 640 || 360;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const data = canvas.toDataURL("image/jpeg", 0.5).split(",")[1];
    ws.send(JSON.stringify({ type: "frame", data }));
  };

  const sendChat = () => {
    const t = text.trim();
    if (!t) return;
    if (!wsRef.current || wsRef.current.readyState !== 1) {
      toast.error("Live chat is not connected yet");
      return;
    }
    wsRef.current.send(JSON.stringify({ type: "chat", text: t }));
    setText("");
  };

  const setSpotlight = (id) => {
    setSpotlightId(id);
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type: "spotlight", product_id: id }));
  };

  const stopEverything = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    stopAudio();
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (wsRef.current) wsRef.current.close();
  };

  const endLive = async () => {
    try { if (session) await api.post(`/live/sessions/${session.id}/end`); } catch { /* ignore */ }
    stopEverything();
    setCamera("off");
    setMic("off");
    setLive(false);
    setSession(null);
    toast.success("Live ended");
  };

  if (!live) {
    return (
      <div className="max-w-[1000px] mx-auto px-6 lg:px-10 pt-28 pb-24">
        <div className="flex items-center gap-2 text-[11px] tracking-luxe uppercase text-aura-accent mb-2">
          <Radio className="w-3.5 h-3.5" /> Live selling
        </div>
        <h1 className="text-3xl font-semibold mb-2">Go live</h1>
        <p className="text-sm text-aura-muted mb-8">Broadcast from your camera, chat with shoppers in real time and spotlight products they can buy instantly.</p>

        <div className="bg-aura-surface border border-aura-border rounded-lg p-6 space-y-5">
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Stream title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Evening gowns drop — tonight only" data-testid="live-title-input" className="mt-2 w-full px-3 py-2.5 border border-aura-border focus:border-aura-accent outline-none text-sm rounded-lg" />
          </div>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Feature products ({selected.length})</label>
            {products.length === 0 ? (
              <p className="mt-2 text-sm text-aura-muted">You have no products yet. Add some first.</p>
            ) : (
              <div className="mt-3 max-h-72 overflow-y-auto grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 pr-1">
                {products.map((p) => (
                  <button key={p.id} type="button" onClick={() => toggleProduct(p.id)} data-testid={`live-select-${p.id}`} className={`text-left border rounded-lg overflow-hidden transition-all ${selected.includes(p.id) ? "border-aura-accent ring-1 ring-aura-accent" : "border-aura-border"}`}>
                    <div className="aspect-[3/4] bg-aura-sand"><img src={imgUrl(p.images?.[0])} alt={p.title} className="w-full h-full object-cover" /></div>
                    <div className="p-1.5 text-[11px] truncate">{p.title}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="border border-aura-border rounded-lg p-4">
            <div className="text-[10px] tracking-luxe uppercase text-aura-muted">Camera</div>
            <p className="mt-2 text-sm text-aura-muted">Shoppers see your camera and hear your microphone after you allow both on this page.</p>
            <button type="button" onClick={allowCamera} data-testid="allow-camera-btn" className="mt-3 px-4 py-2 border border-aura-border rounded-full text-sm hover:border-aura-accent">
              {camera === "on" && mic === "on" ? "Camera and microphone on" : "Allow camera and microphone"}
            </button>
            {camera === "blocked" && (
              <p className="mt-2 text-sm text-aura-accent">Camera is blocked. Click the camera icon in the address bar, choose Allow, then press the button again.</p>
            )}
            {mic === "blocked" && (
              <p className="mt-2 text-sm text-aura-accent">Microphone is blocked. Click the camera icon in the address bar, allow the microphone, then press the button again.</p>
            )}
            {camera === "missing" && (
              <p className="mt-2 text-sm text-aura-accent">No camera was found. Check that a camera is connected, then press Allow camera again.</p>
            )}
          </div>
          <button onClick={startLive} disabled={starting} data-testid="start-live-btn" className="w-full py-3.5 btn-accent text-sm font-medium flex items-center justify-center gap-2 rounded-lg disabled:opacity-60">
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} Start broadcasting
          </button>
        </div>
      </div>
    );
  }

  const selectedProducts = products.filter((p) => selected.includes(p.id));

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-16">
      <canvas ref={canvasRef} className="hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="relative aspect-video bg-aura-accent rounded-xl overflow-hidden">
            <video ref={videoRef} muted playsInline autoPlay className="w-full h-full object-cover" />
            {camera !== "on" && selectedProducts[0] && (
              <>
                <img src={imgUrl(selectedProducts[0].images?.[0])} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                  <p className="text-sm text-white">Camera is off. Shoppers see this product photo.</p>
                  <button type="button" onClick={allowCamera} data-testid="allow-camera-live-btn" className="px-4 py-2 rounded-full bg-white text-aura-ink text-sm">
                    Allow camera and microphone
                  </button>
                  {camera === "blocked" && (
                    <p className="text-xs text-white/80 max-w-sm">Click the camera icon in the address bar, choose Allow, then press Allow camera again.</p>
                  )}
                </div>
              </>
            )}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="neon-live rounded-full bg-aura-accent text-white text-[10px] font-semibold tracking-luxe uppercase px-3 py-1 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-white dot-blink" /> On air</span>
              <span className="neon-count rounded-full glass-dark text-white text-[10px] px-2.5 py-1 border">{viewers} watching</span>
            </div>
            <button onClick={endLive} data-testid="end-live-btn" className="absolute top-4 right-4 bg-aura-surface text-aura-ink text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5"><StopCircle className="w-3.5 h-3.5" /> End</button>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="font-serif text-2xl">{session?.title}</div>
            {session?.id && (
              <button
                type="button"
                onClick={() => {
                  const url = `${window.location.origin}/live/${session.id}`;
                  navigator.clipboard.writeText(url).then(() => toast.success("Shopper link copied")).catch(() => toast.message(url));
                }}
                className="text-xs px-3 py-1.5 border border-aura-border rounded-full hover:border-aura-accent"
              >
                Copy shopper link
              </button>
            )}
          </div>
          {camera === "on" && mic !== "on" && (
            <button type="button" onClick={allowCamera} className="mt-3 text-sm text-aura-accent">
              Microphone is off. Shoppers cannot hear you. Allow microphone
            </button>
          )}
          <div className="mt-4">
            <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-2">Spotlight a product</div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {selectedProducts.map((p) => (
                <button key={p.id} onClick={() => setSpotlight(p.id)} data-testid={`spotlight-${p.id}`} className={`shrink-0 w-20 border rounded-lg overflow-hidden ${spotlightId === p.id ? "border-aura-accent ring-1 ring-aura-accent" : "border-aura-border"}`}>
                  <div className="aspect-[3/4] bg-aura-sand"><img src={imgUrl(p.images?.[0])} alt={p.title} className="w-full h-full object-cover" /></div>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-aura-surface border border-aura-border rounded-xl flex flex-col h-[70vh]">
          <div className="px-5 py-4 border-b border-aura-border font-serif text-lg">Live chat</div>
          <div ref={chatRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
            {messages.map((m, i) => m.system ? (
              <div key={i} className="text-xs text-aura-muted italic">{m.system}</div>
            ) : (
              <div key={i} className="text-sm"><span className="font-medium">{m.name}: </span>{m.text}</div>
            ))}
          </div>
          <div className="p-3 border-t border-aura-border flex items-center gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Message your viewers…" className="flex-1 px-3 py-2 border border-aura-border rounded-full outline-none text-sm" />
            <button onClick={sendChat} className="w-9 h-9 rounded-full btn-accent flex items-center justify-center"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
